import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { API_ENDPOINTS } from 'core/constant/API_ENDPOINTS';
import DataTableWrapper from 'components/common/DataTableWrapper';
import ConfirmDeleteModal from 'components/common/modal/ConfirmDeleteModal';
import EditProjectModal from 'components/admin/common/modal/projects/EditProjectModal';
import useModalState from 'core/hooks/state/useModalState';
import { validateForm } from 'core/utils/validation';
import useCrud from 'core/hooks/api/useCrud';
import { Alert } from 'react-bootstrap';
import usePost from 'core/hooks/api/usePost';
import useDelete from 'core/hooks/api/useDelete';
import moment from 'moment';

const Projects = () => {
    const {
        fetchData: data,
        fetchError: error,
        refetch,
        postData: createProjectData,
        postError: creatingError,
        postLoading: creating,
        putData: updateProjectData,
        putError: updatingError,
        putLoading: updating,
        deleteData: deleteProjectData,
        deleteLoading: deleting,
        deleteError: deletingError,
        clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.PROJECTS);

    const {
        // fetchError: error,
        // refetch,
        postData: createProjectDetail,
        postLoading: creatingProjectDetail,
        // postError: creatingError,
        // postLoading: creating,
        putData: updateProjectDetail,
        putLoading: updatingProjectDetail,
        // putError: updatingError,
        // putLoading: updating,
        deleteData: deleteProjectDetail,
        deleteLoading: deletingProjectDetail
        // deleteLoading: deleting,
        // deleteError: deletingError,
        // clearErrors: clearServerErrors
    } = useCrud(API_ENDPOINTS.PRODUCT_USED);


    const { fetchData: productData } = useCrud(API_ENDPOINTS.PRODUCTS);
    const { fetchData: clientData } = useCrud(API_ENDPOINTS.CLIENTS);
    const { postData: createMediaItem, loading: uploadingImages } = usePost(API_ENDPOINTS.MEDIA_ITEMS);
    const { deleteData: deleteMediaItem } = useDelete(API_ENDPOINTS.MEDIA_ITEMS);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState(null);

    const handleCloseDeleteProject = () => {
        setErrors({});
        clearServerErrors();
    };

    const handleCloseEditProject = () => {
        setErrors({});
        clearServerErrors();
    };

    const { isOpen: isOpenDeleteProject, modalData: projectToDelete, openModal: openDeleteProject, closeModal: closedDeleteProject } = useModalState({
        onClose: handleCloseDeleteProject
    });

    const { isOpen: isOpenEditProject, modalData: selectedProject, openModal: openEditProject, closeModal: closedEditProject } = useModalState({
        onClose: handleCloseEditProject
    });

    const validationRules = [
        { field: 'title', method: 'required' },
        { field: 'category', method: 'required' },
        { field: 'banner_image', method: ['required', 'imageFormat'] },
        { field: 'date', method: 'required' },
        { field: 'location_link', method: ['required', 'validUrl'] },
        { field: 'location', method: 'required' },
        { field: 'description', method: 'required' },
        { field: 'client_id', method: 'required' }
    ];

    const validationFeaturesRules = [
        { field: 'product_id', method: 'required' },
    ];

    const clientsOptions = useMemo(() => {
        if (clientData?.length > 0) {
            return clientData.map(client => ({
                label: client.name,
                value: client.id
            }));
        }
    }, [clientData]);

    const productOptions = useMemo(() => {
        if (productData?.length > 0) {
            return productData.map(product => ({
                label: product.title,
                value: product.id
            }));
        } else {
            return [{ label: 'Default', value: 1 }, { label: 'Default', value: 2 }];
        }
    }, [productData]);

    const handleDeleteMediaItem = async (id) => {
        try {
            await deleteMediaItem(id);
            refetch();
        } catch (error) {
            console.error(error);
        }
    };

    const handleModalSave = useCallback(async (updatedProject, galleryImages, features, featuresToDelete) => {
        setSuccessMessage(null);
        clearServerErrors();
        const formErrors = validateForm(updatedProject, validationRules);
        const featuresErrors = features.map((item, index) => {
            const featureErrors = validateForm(item, validationFeaturesRules);
            return Object.keys(featureErrors).reduce((acc, key) => {
                if (!acc[index]) acc[index] = {};
                acc[index][key] = featureErrors[key];
                return acc;
            }, {});
        }).filter(error => Object.keys(error).length > 0);

        const combinedErrors = { ...formErrors, ...featuresErrors.reduce((acc, curr) => ({ ...acc, ...curr }), {}) };

        if (Object.keys(combinedErrors).length > 0) {
            setErrors(combinedErrors);
            return;
        }

        setErrors({});
        const forms = galleryImages
            ?.filter((image) => {
                const fileType = image?.file?.type || image?.file?.path.split('.').pop();
                return image.file && image.file.path && !image.file.path.startsWith('storage/') && (fileType.startsWith('image/') || ['png', 'jpg', 'jpeg', 'gif'].includes(fileType));
            })
            ?.map((image) => ({
                blog_id: '',
                project_id: updatedProject.id || null,
                type: 'image',
                src: image.file,
            }));

        try {
            const formattedProject = {
                ...updatedProject,
                date: moment(updatedProject.date).format('YYYY-MM-DD')
            };
            
            if (updatedProject.id) {
                const { media_item, ...projectData } = formattedProject;
                await updateProjectData(updatedProject.id, projectData, true);
                if (forms.length > 0) {
                    for (const form of forms) {
                        form.project_id = updatedProject.id;
                        await createMediaItem(form, true);
                    }
                }
                for (const feature of features) {
                    feature.project_id = updatedProject.id;
                    if (feature.id) {
                        await updateProjectDetail(feature.id, feature);
                    } else {
                        await createProjectDetail(feature);
                    }
                }
                for (const featureId of featuresToDelete) {
                    await deleteProjectDetail(featureId);
                }
                setSuccessMessage("Proyecto actualizado con éxito.");
            } else {
                const createdProject = await createProjectData(formattedProject, true);
                if (forms.length > 0) {
                    for (const form of forms) {
                        form.project_id = createdProject.id;
                        await createMediaItem(form, true);
                    }
                }
                for (const feature of features) {
                    feature.project_id = createdProject.id;
                    await createProjectDetail(feature);
                }
                setSuccessMessage("Proyecto creado con éxito.");
            }
            closedEditProject();
            setErrors({});
            refetch();
        } catch (error) {
            console.error(error);
        }
    }, [updateProjectData, createProjectData, createMediaItem, createProjectDetail, updateProjectDetail, deleteProjectDetail, refetch]);

    const handleCreateClick = useCallback(() => {
        setErrors({});
        const newProject = {
            title: '',
            category: '',
            banner_image: '',
            date: '',
            location: '',
            description: '',
            client_id: ''
        };
        openEditProject(newProject);
    }, []);

    const handleDeleteConfirm = useCallback(async () => {
        setSuccessMessage(null);
        if (projectToDelete) {
            try {
                await deleteProjectData(projectToDelete.id);
                setSuccessMessage("Proyecto eliminado con éxito.");
                refetch();
                closedDeleteProject();
            } catch (error) {
                console.error(error);
            }
        }
    }, [projectToDelete, deleteProjectData, refetch]);

    const columns = useMemo(() => [
        { field: 'title', header: 'Título', sortable: true },
        { field: 'category', header: 'Categoría', sortable: true },
        { field: 'banner_image', header: 'Imagen de Banner', sortable: true },
        { field: 'date', header: 'Fecha', sortable: true },
        { field: 'description', header: 'Descripción', sortable: true },
        { field: 'client_id', header: 'Cliente', sortable: true }
    ], []);

    const memoizedGalleryImages = useMemo(() => selectedProject?.media_item || [], [selectedProject]);

    if (error) return <p>Error: {error.message}</p>;

    return (
        <div>
            <div className="row w-100 mb-2">
                <div className="p-0 sub-title Service mb-2">
                    <div className="d-flex justify-content-between align-items-center mt-3">
                        <h5 className="m-0 text-title">Proyectos</h5>
                        <button className="btn btn-primary" onClick={handleCreateClick} disabled={updating || creating}>
                            <i className="fa fa-plus-circle me-2" aria-hidden="true" />
                            Agregar Proyecto
                        </button>
                    </div>
                </div>
                <hr className="mx-0" style={{ border: '2px', margin: '5px' }} />
            </div>
            {successMessage && <Alert variant="success">{successMessage}</Alert>}
            <DataTableWrapper
                data={data || []}
                columns={columns}
                onEdit={openEditProject}
                onDelete={openDeleteProject}
                paginator={false}
                totalRecords={data?.length || 0}
                emptyMessage="No se encontraron registros."
                sortField="title"
                sortOrder={1}
                lazy={false}
                showGridlines={true}
            />
            <EditProjectModal
                show={isOpenEditProject}
                project={selectedProject}
                handleClose={closedEditProject}
                handleSave={handleModalSave}
                updating={updating || creating || uploadingImages || updatingProjectDetail || creatingProjectDetail || deletingProjectDetail}
                errors={errors}
                requestError={creatingError || updatingError}
                deleteMediaItem={handleDeleteMediaItem}
                initialImages={memoizedGalleryImages}
                clientsOptions={clientsOptions}
                productOptions={productOptions}
            />
            <ConfirmDeleteModal
                show={isOpenDeleteProject}
                onHide={closedDeleteProject}
                onConfirm={handleDeleteConfirm}
                deleting={deleting}
                error={deletingError}
            />
        </div>
    );
};

export default React.memo(Projects);